import React from "react";
import { Award, Users, BookOpen, Heart, Quote, ArrowRight, TrendingUp, Landmark } from "lucide-react";

const Stitch = ({ className = "", color = "#E0A526" }) => (
  <svg className={className} viewBox="0 0 100 4" preserveAspectRatio="none">
    <line
      x1="0" y1="2" x2="100" y2="2"
      stroke={color} strokeWidth="2" strokeDasharray="6 5" strokeLinecap="round"
    />
  </svg>
);

const detailedStats = [
  {
    icon: Users,
    value: "5,000+",
    label: "Trainees Empowered",
    description: "Women, youth, and rural community members trained in employable skills since 2015.",
  },
  {
    icon: Award,
    value: "82%",
    label: "Employment Rate",
    description: "Graduates who have successfully secured jobs or started self-reliant micro-enterprises.",
  },
  {
    icon: BookOpen,
    value: "15+",
    label: "Vocational Labs",
    description: "Equipped centers offering tailoring, IT, electronics wiring, and wellness courses.",
  },
  {
    icon: Landmark,
    value: "100%",
    label: "CSR Compliance",
    description: "Fully audited audits, voluntary disclosures, and transparent project-level reporting.",
  },
];

const spotlights = [
  {
    name: "Priyasri K.",
    trade: "Tailoring & Boutique Management",
    village: "Bagayam, Vellore",
    story: "As a homemaker, Priyasri joined our 6-month tailoring program. Today, she runs her own boutique home enterprise, stitching garments for local clients and earning a steady income that funds her children's schooling.",
    quote: "MB Foundation gave me not just skills, but the financial independence I always dreamed of.",
  },
  {
    name: "Ramesh Kumar",
    trade: "Computer & IT Literacy",
    village: "Sathuvachari, Vellore",
    story: "Ramesh completed our IT Literacy certificate course in 2022. Wielding MS Office and administrative digital skills, he secured a position as a billing executive at a local logistics firm.",
    quote: "The practical computing course was my bridge to a structured career.",
  },
  {
    name: "Meenakshi S.",
    trade: "Organic Agriculture & Bio-composting",
    village: "Odugathur, Tamil Nadu",
    story: "A smallholder farmer, Meenakshi attended our agri-skills training camp. She switched to vermicomposting and bio-pest controls, slashing fertilizer costs by 40% and obtaining premium organic sales.",
    quote: "Learning natural farming restored our soil health and doubled our farming margins.",
  },
];

const ImpactPage = () => {
  return (
    <div className="bg-[#FBF9F3] min-h-screen pt-[84px] pb-16">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600;9..144,700&family=IBM+Plex+Sans:wght@300;400;500;600;700&family=IBM+Plex+Mono:wght@500&display=swap');
        .mbf-display { font-family: 'Fraunces', serif; }
        .mbf-body { font-family: 'IBM Plex Sans', sans-serif; }
        .mbf-mono { font-family: 'IBM Plex Mono', monospace; }
      `}</style>

      {/* Hero Header */}
      <section className="bg-[#082440] text-white py-20 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.03] pointer-events-none"
          style={{
            backgroundImage:
              "repeating-linear-gradient(0deg, #fff 0 1px, transparent 1px 34px), repeating-linear-gradient(90deg, #fff 0 1px, transparent 1px 34px)",
          }}
        />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10 text-center">
          <span className="mbf-mono text-[12px] tracking-[0.2em] uppercase text-[#E0A526]">
            Our Achievements
          </span>
          <h1 className="mbf-display text-4xl sm:text-5xl font-semibold mt-4 tracking-tight">
            Impact Stitched Into Communities
          </h1>
          <Stitch className="w-32 h-2 mx-auto mt-6" color="#E0A526" />
          <p className="mbf-body mt-6 text-blue-100/70 max-w-2xl mx-auto text-base sm:text-lg leading-relaxed">
            Measuring our success not in files or audits, but in the self-reliance and dignity of the thousands we train.
          </p>
        </div>
      </section>

      {/* Metrics Grid */}
      <section className="py-20 max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {detailedStats.map((stat, i) => {
            const Icon = stat.icon;
            return (
              <div
                key={i}
                className="bg-white border border-[#0A355C]/10 p-8 flex flex-col justify-between hover:-translate-y-1 transition duration-300 shadow-sm"
              >
                <div>
                  <div className="w-10 h-10 rounded-full border border-[#0A355C] flex items-center justify-center text-[#2E7D32] bg-[#FBF9F3] mb-6">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="mbf-display text-4xl font-bold text-[#0A355C] leading-none mb-3">
                    {stat.value}
                  </h3>
                  <h4 className="mbf-mono text-xs uppercase tracking-wider text-[#2E7D32] font-semibold mb-3">
                    {stat.label}
                  </h4>
                </div>
                <p className="mbf-body text-xs sm:text-sm text-[#6b6355] leading-relaxed mt-2 border-t border-[#0A355C]/10 pt-4">
                  {stat.description}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Trainee Case Studies / Spotlights */}
      <section className="bg-[#F2ECDD] py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="mbf-mono text-[12px] tracking-[0.18em] uppercase text-[#2E7D32]">
              Real Success Stories
            </span>
            <h2 className="mbf-display mt-3 text-3xl font-semibold text-[#0A355C]">
              Trainee Spotlights
            </h2>
            <Stitch className="w-24 h-2 mx-auto mt-4" color="#2E7D32" />
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {spotlights.map((item, i) => (
              <div
                key={i}
                className="bg-white border border-[#0A355C]/10 p-8 flex flex-col justify-between shadow-sm relative"
              >
                <Quote className="absolute top-6 right-6 w-10 h-10 text-[#2E7D32]/10" />
                
                <div>
                  <span className="mbf-mono text-[10px] uppercase text-[#2E7D32] bg-[#FBF9F3] border border-[#0A355C]/10 px-2 py-1 font-semibold rounded-none">
                    {item.trade}
                  </span>
                  
                  <p className="mbf-body italic text-[#514a3d] text-[14.5px] leading-relaxed mt-6 mb-6">
                    "{item.quote}"
                  </p>
                  
                  <div className="border-t border-[#0A355C]/10 pt-6">
                    <h4 className="mbf-display text-lg font-semibold text-[#0A355C]">
                      {item.name}
                    </h4>
                    <p className="mbf-mono text-[11px] text-[#6b6355] mt-1">
                      {item.village}
                    </p>
                  </div>
                </div>

                <p className="mbf-body text-xs text-[#6b6355] mt-4 leading-relaxed bg-[#FBF9F3]/50 p-4 border border-[#0A355C]/5">
                  {item.story}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CSR Impact Partnership CTA */}
      <section className="py-20 max-w-7xl mx-auto px-6 lg:px-8">
        <div
          className="bg-[#0A355C] text-white p-8 md:p-12 relative overflow-hidden"
          style={{ clipPath: "polygon(0 0, 100% 0, 100% 90%, 96% 100%, 0 100%)" }}
        >
          <div
            className="absolute inset-0 opacity-[0.02] pointer-events-none"
            style={{
              backgroundImage:
                "repeating-linear-gradient(0deg, #fff 0 1px, transparent 1px 30px), repeating-linear-gradient(90deg, #fff 0 1px, transparent 1px 30px)",
            }}
          />
          <div className="relative z-10 md:flex justify-between items-center gap-8">
            <div className="max-w-xl">
              <span className="mbf-mono text-[11px] uppercase tracking-wider text-[#E0A526]">
                Scale Your Livelihood Projects
              </span>
              <h3 className="mbf-display text-2xl sm:text-3xl font-semibold mt-3">
                Do you want to support or sponsor a training cycle?
              </h3>
              <p className="mbf-body text-blue-100/70 text-sm mt-3 leading-relaxed">
                We design and execute custom CSR training programs tailored to local industry needs. Sponsor batches of tailors, IT clerks, or organic farmers and track their progress from day one.
              </p>
            </div>
            
            <a
              href="#contact"
              className="mbf-body inline-flex items-center gap-2 bg-[#E0A526] hover:bg-[#eec267] text-[#0A355C] px-8 py-4 font-semibold text-xs uppercase tracking-wider transition mt-6 md:mt-0 shadow-md shrink-0 self-start md:self-center"
            >
              Partner with the Trust
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ImpactPage;
